/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useMemo } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { styles } from '../ReportsScreen.styles';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing';
import type { SalesDailyRow } from '../../domain/reportsTypes';
import { ticketColors } from '../../../shared/theme/ticketColors';
import {
  aggregateSalesDailyForChart,
  chartBucketHint,
  chooseChartBucketMode,
} from '../reportsChartAggregation';
import { formatMoneyCompact } from '../../../shared/format/public-api';

type Props = {
  rows: SalesDailyRow[];
  /** Días del rango seleccionado: decide el bucket (día/semana/mes). Fallback: cantidad de filas. */
  rangeDays?: number;
};

/** Barras simples (solo vistas nativas). Rangos largos: agrupa por semana o mes para legibilidad. */
export function ReportsSalesDailyBars({ rows, rangeDays }: Props) {
  const { mode, aggregated } = useMemo(() => {
    const mode = chooseChartBucketMode(rangeDays ?? rows.length);
    return { mode, aggregated: aggregateSalesDailyForChart(rows, mode) };
  }, [rows, rangeDays]);

  if (aggregated.length === 0) {
    return (
      <View accessibilityLabel="Gráfico de facturación: sin datos agrupados">
        <Text style={styles.caption} accessibilityRole="text">
          No hay series suficientes para el gráfico.
        </Text>
      </View>
    );
  }
  const maxTotal = Math.max(...aggregated.map((r) => r.salesTotal), 1);
  const hint = chartBucketHint(mode);
  const colWidth = mode === 'month' ? 72 : mode === 'week' ? 64 : 56;

  return (
    <View
      style={{ gap: spacing.sm }}
      accessibilityLabel={`Gráfico de facturación ${mode === 'day' ? 'por día' : mode === 'week' ? 'por semana' : 'por mes'}. Deslizá horizontalmente si hay muchas columnas.`}
    >
      <Text style={styles.kpiSheetTitle}>
        {mode === 'day' ? 'Facturación por día' : mode === 'week' ? 'Facturación por semana' : 'Facturación por mes'}
      </Text>
      {hint ? (
        <Text style={[styles.caption, { color: ticketColors.ink3 }]} accessibilityRole="text">
          {hint}
        </Text>
      ) : null}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator
        contentContainerStyle={{ gap: spacing.xs, paddingVertical: spacing.xs, paddingRight: spacing.md }}
      >
        {aggregated.map((r) => {
          const h = Math.max(8, Math.round((r.salesTotal / maxTotal) * 88));
          const dim = r.salesTotal <= 0;
          return (
            <View key={r.key} style={{ alignItems: 'center', width: colWidth }}>
              <View
                style={{
                  height: 104,
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  width: '100%',
                }}
                accessibilityLabel={`${r.label}: ${Math.round(r.salesTotal)} pesos`}
              >
                <View
                  style={{
                    width: '62%',
                    maxWidth: 30,
                    height: h,
                    borderTopLeftRadius: 5,
                    borderTopRightRadius: 5,
                    backgroundColor: dim ? ticketColors.rule : ticketColors.gold,
                    opacity: dim ? 1 : 0.85,
                  }}
                />
              </View>
              <Text style={[styles.caption, { marginTop: 4, fontSize: 11 }]} numberOfLines={2}>
                {r.label}
              </Text>
              <Text style={[styles.caption, { fontSize: 10, color: ticketColors.ink2 }]} numberOfLines={1}>
                {formatMoneyCompact(Math.round(r.salesTotal))}
              </Text>
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
}
